import type { DocumentType } from '../types';

/**
 * Convert a File to base64 string
 */
export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
}

/**
 * Classify document type based on filename (local fallback)
 */
export function classifyDocument(fileName: string): { type: DocumentType; confidence: number } {
  const lowerName = fileName.toLowerCase();

  const patterns: Array<{ keywords: string[]; type: DocumentType }> = [
    { keywords: ['income', 'salary', 'payslip', 'pay stub', 'nomina', 'sueldo', 'ingreso'], type: 'income_statement' },
    { keywords: ['bank', 'statement', 'account', 'extracto', 'cuenta', 'bancario'], type: 'bank_statement' },
    { keywords: ['employment', 'employer', 'trabajo', 'empleo', 'contract', 'laboral'], type: 'employment_letter' },
    { keywords: ['passport', 'dni', 'id', 'identity', 'cedula', 'license', 'identificacion'], type: 'identity_document' },
    { keywords: ['address', 'utility', 'factura', 'domicilio', 'proof', 'residence', 'direccion'], type: 'proof_of_address' },
    { keywords: ['hardship', 'dificultad', 'declaration', 'carta', 'situacion'], type: 'hardship_declaration' },
    { keywords: ['payment', 'receipt', 'pago', 'recibo', 'transfer', 'comprobante'], type: 'payment_proof' },
    { keywords: ['medical', 'doctor', 'hospital', 'medico', 'health', 'salud'], type: 'medical_certificate' },
    { keywords: ['unemployment', 'desempleo', 'paro', 'jobless', 'cesante'], type: 'unemployment_certificate' },
  ];

  for (const pattern of patterns) {
    if (pattern.keywords.some(kw => lowerName.includes(kw))) {
      return { type: pattern.type, confidence: 0.75 };
    }
  }

  return { type: 'other', confidence: 0.5 };
}

/**
 * Format file size to human readable string
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Validate if file type is allowed
 */
export function validateFileType(file: File): boolean {
  const allowedTypes = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/msword', // .doc
    'image/png',
    'image/jpeg',
    'image/jpg',
  ];

  // Also check by extension for edge cases
  const allowedExtensions = ['.pdf', '.docx', '.doc', '.png', '.jpg', '.jpeg'];
  const extension = '.' + file.name.split('.').pop()?.toLowerCase();

  return allowedTypes.includes(file.type) || allowedExtensions.includes(extension);
}

/**
 * Validate file size
 */
export function validateFileSize(file: File, maxSizeMB: number = 10): boolean {
  const maxBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxBytes;
}

/**
 * Generate UUID v4
 */
export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get file extension from filename
 */
export function getFileExtension(fileName: string): string {
  return fileName.split('.').pop()?.toLowerCase() || '';
}

/**
 * Get document type label in Spanish
 */
export function getDocumentTypeLabel(type: DocumentType): string {
  const labels: Record<DocumentType, string> = {
    income_statement: 'Comprobante de Ingresos',
    bank_statement: 'Extracto Bancario',
    employment_letter: 'Carta de Empleo',
    identity_document: 'Documento de Identidad',
    proof_of_address: 'Comprobante de Domicilio',
    hardship_declaration: 'Declaración de Dificultad',
    payment_proof: 'Comprobante de Pago',
    medical_certificate: 'Certificado Médico',
    unemployment_certificate: 'Certificado de Desempleo',
    other: 'Otro Documento',
  };
  return labels[type] || type;
}

/**
 * Get MIME type from file extension
 */
export function getMimeType(fileName: string): string {
  const ext = getFileExtension(fileName);
  const mimeTypes: Record<string, string> = {
    pdf: 'application/pdf',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    doc: 'application/msword',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
  };
  return mimeTypes[ext] || 'application/octet-stream';
}
